import { World, makeBody, KIND_HEAD, KIND_BODY } from '@/sim/world';
import type { WeaponSpec } from '@/content/weapons';

export interface PointerState {
  x: number;
  y: number;
  planted: boolean;
  active: boolean;
}

export class Hero {
  readonly head: number;
  readonly body: number;
  readonly joints: number[] = [];
  private readonly spec: WeaponSpec;
  private readonly world: World;

  constructor(world: World, spec: WeaponSpec, x: number, y: number) {
    this.world = world;
    this.spec = spec;

    this.body = world.add(makeBody(x, y, spec.bodyRadius, spec.bodyMass, spec.bodyFriction, KIND_BODY));

    const segment = spec.haftLength / spec.links;
    let previous = this.body;
    for (let i = 1; i < spec.links; i += 1) {
      const t = i / spec.links;
      const joint = world.add(
        makeBody(x + spec.haftLength * t, y, 0.12, spec.headMass * 0.16, spec.headFriction, 0)
      );
      this.joints.push(joint);
      world.link(previous, joint, segment, spec.compliance, false);
      previous = joint;
    }

    this.head = world.add(
      makeBody(x + spec.haftLength, y, spec.headRadius, spec.headMass, spec.headFriction, KIND_HEAD)
    );
    world.link(previous, this.head, segment, spec.compliance, false);
  }

  fixedUpdate(dt: number, pointer: PointerState): void {
    const head = this.world.bodies[this.head];
    const body = this.world.bodies[this.body];

    const spec = this.spec;
    const swing = Math.hypot(head.vx, head.vy);
    const loose = Math.min(1, Math.max(0, (swing - spec.breakSpeed) / spec.breakSpan));
    body.grip = pointer.planted ? spec.plantedGrip : spec.gripFloor + loose * (1 - spec.gripFloor);
    body.friction = loose > 0 ? spec.bodyFriction : spec.gripFriction;

    if (!pointer.active) return;

    let wantX = (pointer.x - head.x) / spec.tau;
    let wantY = (pointer.y - head.y) / spec.tau;
    const want = Math.hypot(wantX, wantY);
    if (want > spec.maxSpeed) {
      const k = spec.maxSpeed / want;
      wantX *= k;
      wantY *= k;
    }

    let dvx = wantX - head.vx;
    let dvy = wantY - head.vy;
    const dv = Math.hypot(dvx, dvy);
    const budget = spec.maxAccel * dt;
    if (dv > budget) {
      const k = budget / dv;
      dvx *= k;
      dvy *= k;
    }

    head.vx += dvx;
    head.vy += dvy;
  }

  headSpeed(): number {
    const head = this.world.bodies[this.head];
    return Math.hypot(head.vx, head.vy);
  }
}
